import {
	IconButton,
	Tab,
	Tabs,
	Tooltip,
	Paper,
	Box,
	Typography,
	CircularProgress,
} from "@material-ui/core";
import { Table } from "src/utils/components/Table";
import { Settings, Warning } from "@material-ui/icons";
import { ChevronRight, Edit, Trash2 } from "react-feather";
import { useCallback, useEffect, useState } from "react";
import { colorsCheckPro } from "src/theme/variants";
import { useResponsiveQuery } from "src/utils/hooks/useResponsiveQuery";
import { PercentageCell } from "../Misc/PercentageCell";
import { NeoChecklistSearchBar } from "./SearchBar";
import { useProjectsOT } from "src/hooks/redux/useProjectsOT";
import { DropdownMenu } from "../../Generic/DropdownMenu";
import { Opener } from "../../CheckPro/Opener";
import { useHistory } from "react-router-dom";
import { getRef } from "src/utils/firebase";
import { ChecklistOT } from "src/models/OT/Checklist";
import { recalculateAllPendingItems } from "src/utils/pendingItemsCalculator";
import { NeoChecklistDeleteDialog } from "./Dialog/Delete";
import { NeoChecklistEditDialog } from "./Dialog/Edit";
import { NeoChecklistPlacesDialog } from "./Dialog/Places";
import { useSelector } from "src/redux/store";

export const ChecklistTableOT = () => {
	const { user } = useSelector((s) => s.authReducer);
	const users = useSelector((s) => s.usersReducer.users);

	const isSmall = useResponsiveQuery("down", "xs");

	const history = useHistory();

	const [dialog, setDialog] = useState(0);
	const [checklistsWithYellowResponses, setChecklistsWithYellowResponses] = useState<Record<string, boolean>>({});
	const [checklistsWithBlueYellowResponses, setChecklistsWithBlueYellowResponses] = useState<Record<string, boolean>>({});
	const [checklistsWithInProcessResponses, setChecklistsWithInProcessResponses] = useState<Record<string, boolean>>({});
	const [isMigrating, setIsMigrating] = useState(false);

	const { set, project, get, selected, toggle } =
		useProjectsOT();

	const [selectedType, setSelectedType] =
		useState<ChecklistOT["Tipo"]>("Obra Gruesa");

	const [checklistName, setChecklistName] = useState("");

	// ELIMINADO: No más migraciones automáticas silenciosas
	// La lógica de P debe leerse directamente del campo BD sin sincronización
	/*
	useEffect(() => {
		// Migración automática eliminada para evitar procesos no deseados
	}, [project.checklists, isMigrating]);
	*/

	// Check for checklists with yellow and blue-yellow responses
	useEffect(() => {
		if (!project.checklists) return;

		const checkForResponses = () => {
			try {
				// No need to set loading state since we're using already loaded data
				// setIsCheckingResponses(true);

				// Get data from localStorage for yellow and blue-yellow responses
				const storedYellowData = localStorage.getItem('placesWithYellowResponses');
				const storedBlueYellowData = localStorage.getItem('placesWithBlueYellowResponses');

				// Initialize empty objects for all types of responses
				const checklistsWithYellow: Record<string, boolean> = {};
				const checklistsWithBlueYellow: Record<string, boolean> = {};
				const checklistsWithInProcess: Record<string, boolean> = {}; // This will be populated directly from the server

				// Process yellow responses
				if (storedYellowData) {
					try {
						const yellowResponsesData = JSON.parse(storedYellowData);

						// Check each checklist
						project.checklists.forEach(checklist => {
							const checklistId = getRef(checklist).id;
							const hasYellowResponses = yellowResponsesData[checklistId] &&
								Object.keys(yellowResponsesData[checklistId]).length > 0;

							checklistsWithYellow[checklistId] = hasYellowResponses;
						});
					} catch (parseError) {
						console.error("Invalid yellow responses localStorage data:", parseError);
						localStorage.removeItem('placesWithYellowResponses');
					}
				}

				// Process blue-yellow responses
				if (storedBlueYellowData) {
					try {
						const blueYellowResponsesData = JSON.parse(storedBlueYellowData);

						// Check each checklist
						project.checklists.forEach(checklist => {
							const checklistId = getRef(checklist).id;
							const hasBlueYellowResponses = blueYellowResponsesData[checklistId] &&
								Object.keys(blueYellowResponsesData[checklistId]).length > 0;

							checklistsWithBlueYellow[checklistId] = hasBlueYellowResponses;
						});
					} catch (parseError) {
						console.error("Invalid blue-yellow responses localStorage data:", parseError);
						localStorage.removeItem('placesWithBlueYellowResponses');
					}
				}

				// Process in-process responses - use pre-calculated hasPendingItems field from already loaded data
				// No need for additional Firestore queries since the data is already available
				project.checklists.forEach(checklist => {
					const checklistId = getRef(checklist).id;
					
					// Use pre-calculated hasPendingItems field directly from the already loaded checklist data
					const hasInProcessResponses = (checklist as any).hasPendingItems || false;

					console.log(`[P Calculation] Checklist ${checklistId} (${checklist.Nombre}) - hasPendingItems:`, (checklist as any).hasPendingItems, '(type:', typeof (checklist as any).hasPendingItems, ') -> hasInProcessResponses:', hasInProcessResponses);
					
					// If hasPendingItems is undefined, it means the field hasn't been calculated yet
					if ((checklist as any).hasPendingItems === undefined) {
						console.warn(`[P Calculation] Checklist ${checklistId} (${checklist.Nombre}) - hasPendingItems field is undefined. Field needs to be populated by system.`);
					}

					checklistsWithInProcess[checklistId] = hasInProcessResponses;
				});

				// Update state once at the end to avoid multiple re-renders
				setChecklistsWithYellowResponses(checklistsWithYellow);
				setChecklistsWithBlueYellowResponses(checklistsWithBlueYellow);
				setChecklistsWithInProcessResponses(checklistsWithInProcess);
			} catch (error) {
				console.error("Error checking for checklist responses:", error);
			}
		};

		checkForResponses();
	}, [project.checklists]);

	useEffect(() => {
		if (!selected.project) return;
		get.project.checklists(selectedType);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selected.project, selectedType]);

	const handleRedirectToPlaces = (
		checklist: ChecklistOT
	) => {
		const checklistRef = getRef(checklist);
		const projectRef = getRef(selected.project);

		set.checklist(checklist);
		history.push(`${projectRef.id}/${checklistRef.id}`);
	};

	const getPercentage = (checklist: ChecklistOT) => {
		const { CantidadCompletada = 0, Cantidad = 1 } =
			checklist;

		return Math.round(
			(CantidadCompletada * 100) / (Cantidad || 1)
		);
	};

	// ELIMINADO: No más verificación de migración automática
	// La lógica de P debe trabajar directamente con datos existentes en BD
	const needsAutoMigration = () => {
		// Siempre retornar false para evitar migraciones automáticas
		return false;
	};

	// ELIMINADO: Función de migración deshabilitada para evitar sincronización silenciosa
	const handleMigratePendingItems = async (isAutomatic = false) => {
		console.log('[ChecklistTable] Migration function disabled - no automatic synchronization');
		
		// No ejecutar migración automática
		if (isAutomatic) {
			console.log('[ChecklistTable] Automatic migration blocked');
			return;
		}
		
		// Solo permitir migración manual explícita del usuario (si fuera necesaria)
		console.log('[ChecklistTable] Manual migration would require explicit user action');
		return;
		
		/* CÓDIGO DE MIGRACIÓN DESHABILITADO
		setIsMigrating(true);
		try {
			const projectRef = getRef(selected.project);
			const empresaId = projectRef.parent.parent?.id;
			await recalculateAllPendingItems(empresaId, projectRef.id);
			// ... resto del código comentado
		} finally {
			setIsMigrating(false);
		}
		*/
	};


	const getMenuOptions = useCallback(
		(value: ChecklistOT) => [
			{
				label: "Editar",
				icon: <Edit color={colorsCheckPro.primary} />,
				disabled: user?.tipoUsuario === "UsuarioEmpresa",
				onClick: () => {
					set.checklist(value);
					setDialog(2);
				},
			},
			{
				label: "Eliminar",
				disabled: user?.tipoUsuario === "UsuarioEmpresa",
				icon: <Trash2 color="red" />,
				onClick: () => {
					set.checklist(value);
					setDialog(1);
				},
			},
			{
				label: "Asignar espacios",
				disabled: user?.tipoUsuario === "UsuarioEmpresa",
				icon: (
					<Settings htmlColor={colorsCheckPro.primary} />
				),
				onClick: () => {
					set.checklist(value);
					setDialog(3);
				},
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[project.checklists]
	);

	return (
		<>
			<Paper elevation={3}>
				<Tabs
					style={{ alignSelf: "center", marginBottom: 10 }}
					value={selectedType}
					onChange={(_, v) => setSelectedType(v)}
				>
					<Tab
						label="OBRA GRUESA"
						value="Obra Gruesa"
						style={{ width: 200 }}
					/>
					<Tab
						label="TERMINACIONES"
						value="Terminaciones"
						style={{ width: 200 }}
					/>
				</Tabs>
			</Paper>


			<Paper elevation={4} style={{ flexGrow: 0 }}>
				<Table
					emptyMessage="Asigne al menos una lista de chequeo"
					stripped
					loading={!project.checklists}
					elementsProps={{
						TableBodyProps: {},
						TablePaginationProps: {
							backIconButtonProps: { size: "small" },
							nextIconButtonProps: { size: "small" },
						} as any,
						TableProps: {
							size: "small",
							style: { height: "100%" },
						},
						TableHeadProps: {
							style: {
								backgroundColor: colorsCheckPro.primary,
							},
						},
					}}
					disableExporting
					list={project.checklists ?? []}
					actions={
						!selected.project?.Eliminado && (
							<NeoChecklistSearchBar
								value={checklistName}
								onChange={(val) => setChecklistName(val)}
								type={selectedType}
								// onSubmit={addRevision}
							/>
						)
					}
					columns={[
						{
							header: "",
							width: 50, // Ancho mínimo ajustado
							align: "center",
							Cell: ({ value }) => {
								const checklistId = getRef(value).id;
								const hasInProcessResponse = checklistsWithInProcessResponses[checklistId];

								// Mostrar óvalo directamente cuando hay respuestas pendientes (sin estado de carga)
								if (hasInProcessResponse) {
									const tooltipTitle = "Este checklist tiene espacios con respuestas en proceso";

									return (
										<Tooltip title={tooltipTitle}>
											<Box
												display="flex"
												alignItems="center"
												justifyContent="center"
												width={16}
												height={16}
												borderRadius="50%"
												bgcolor="#FFFF00"
												border="1px solid rgba(0, 0, 0, 0.5)"
												style={{ margin: '0 auto', position: 'relative' }}
											>
												<Typography
													variant="body2"
													style={{
														color: 'rgba(0, 0, 0, 0.5)',
														fontWeight: 'bold',
														fontSize: 10,
														lineHeight: 1
													}}
												>
													P
												</Typography>
											</Box>
										</Tooltip>
									);
								}

								// Retorna null para celdas vacías cuando no hay estado
								return null;
							},
						},
						{
							header: "Nombre",
							Cell: ({ value }) => {
								return (
									<Box display="flex" alignItems="center">
										{value.Nombre}
									</Box>
								);
							},
						},
						{
							align: "center",
							header: "Cantidad",
							Cell: ({ value }) => <>{value.Cantidad}</>,
						},
						{
							align: "center",
							header: "Unidad",
							Cell: ({ value }) => <>{value.Unidad}</>,
						},
						{
							align: "center",
							header: "Progreso",
							Cell: ({ value }) => (
								<Tooltip
									title={
										<Typography>
											<ul>
												<li>
													Menor o igual del 25%: rojo{" "}
												</li>
												<li>
													Sobre 25% y menos 50%: naranjo
												</li>
												<li>
													Sobre 50% y menos al 75%: amarillo
												</li>
												<li>
													Sobre 75% y menos a 99%: azul
												</li>
												<li>Sobre el 99%: verde</li>
											</ul>
											Si la meta es 50%, y has completado un
											51%, se vería en verde porque
											superaría el 99% RESPECTO A LA META.
										</Typography>
									}
								>
									<Box>
										<PercentageCell
											value={getPercentage(value)}
											aim={value.MetaRevision}
										/>
									</Box>
								</Tooltip>
							),
						},

						{
							align: "right",
							Cell: ({ value }) => (
								<>
									{!selected.project?.Eliminado && (
										<DropdownMenu
											Opener={Opener}
											options={getMenuOptions(value)}
										/>
									)}

									<Tooltip title="Ver espacios">
										<IconButton
											size="small"
											color="primary"
											onClick={() =>
												handleRedirectToPlaces(value)
											}
										>
											<ChevronRight />
										</IconButton>
									</Tooltip>
								</>
							),
						},
					]}
				/>
			</Paper>

			<NeoChecklistDeleteDialog
				open={dialog === 1}
				onClose={() => {
					setDialog(0);
					set.checklist(null);
				}}
				fullScreen={isSmall}
			/>

			<NeoChecklistEditDialog
				open={dialog === 2}
				onClose={() => {
					setDialog(0);
					set.checklist(null);
				}}
			/>

			<NeoChecklistPlacesDialog
				fullScreen
				open={dialog === 3}
				onClose={() => {
					setDialog(0);
					set.checklist(null);
					toggle.assignedPlace([]);
					toggle.place([]);
				}}
			/>
		</>
	);
};
